import { differenceInSeconds } from 'date-fns';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useOptimistic,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import {
  type Bonus,
  type Freespin,
  playerBonusesActivation,
  playerFreespinsActivation,
} from '@/api/common';
import type { GameV1 } from '@/api/games';
import { ButtonClose } from '@/components/buttons';
import { InfoButton } from '@/components/buttons/InfoButton';
import { ImageComponent } from '@/components/Image';
import {
  ProgressBar,
  ValuePosition,
} from '@/components/progressBars/LinearProgressBar';
import { ProgressBarMode } from '@/components/progressBars/LinearProgressBar/styled';
import { addUrlParameter, removeQueryParameter } from '@/helpers/common';
import {
  amountWithCurrencySymbol,
  DEFAULT_CURRENCIES,
} from '@/helpers/currency';
import { mapGameV2ToGameV1 } from '@/helpers/game';
import { BONUS_ID_QUERY_KEY, Modals } from '@/helpers/modals';
import { showTooltip } from '@/helpers/tooltip';
import { getCurrencyCentsAndCode } from '@/helpers/wallet';
import { useNavigation } from '@/hooks/navigation';
import { useCMSGameCategories } from '@/modules/lobby/hooks';
import { useCMSLoyaltyMissions } from '@/modules/loyalty';
import { WagerFreeCashTooltip } from '@/modules/promotions/bonusNotification';
import {
  BONUS_REF_PARAM,
  isBonus,
  PERCENTS_100,
  PROMO_HEADER_OFFSET,
} from '@/modules/promotions/components/PromotionsPage/helpers';
import { useGetCommonStore, useModalsStore } from '@/store';
import { TooltipTypes } from '@/store/tooltipStore';
import { BonusCardButtons } from './BonusCardButtons';
import { BonusCardTimer } from './BonusCardTimer';
import { ScBonusCard } from './styled';
import { BonusTerms } from './Terms';

const MIN_LONG_TEXT = 18;

export interface BonusFlipItemProps {
  bonus: Bonus | Freespin;
}

export const BonusFlipItem: FC<BonusFlipItemProps> = ({ bonus }) => {
  const { t } = useTranslation('bonuses');

  const { categories: gameCategoriesList } = useCMSGameCategories();
  const useCommonStore = useGetCommonStore();
  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const { fetchGamesByIds, fetchBonuses, fetchFreespins, user } =
    useCommonStore(
      useShallow((state) => ({
        fetchGamesByIds: state.fetchGamesByIds,
        fetchBonuses: state.fetchBonuses,
        fetchFreespins: state.fetchFreespins,
        user: state.user,
      }))
    );

  const { navigate } = useNavigation();
  const { push } = useRouter();
  const searchParams = useSearchParams();
  const openModal = useModalsStore.use.open();

  const [isFlipped, toggleFlip] = useState(false);
  const [pendingBonusActivation, setPendingBonusActivation] = useState(false);
  const [pendingFreespinsActivation, setPendingFreespinsActivation] =
    useState(false);
  const [bonusGames, setBonusGames] = useState<GameV1[]>();
  const [pendingBonusGames, setPendingBonusGames] = useState(false);
  const { missions } = useCMSLoyaltyMissions();
  const [optimisticBonus, setOptimisticBonus] = useOptimistic<Bonus | Freespin>(
    bonus
  );
  const ref = useRef<HTMLDivElement | null>(null);
  const [isBonusSelected, setIsBonusSelected] = useState(false);

  const {
    validUntil,
    title,
    currency,
    activatable,
    activatableUntil,
    cancelable,
    id,
    groupKey,
  } = optimisticBonus;

  const isPending = useMemo(
    () =>
      pendingBonusActivation || pendingFreespinsActivation || pendingBonusGames,
    [pendingBonusActivation, pendingFreespinsActivation, pendingBonusGames]
  );

  const isCashBonus = useMemo(
    () => isBonus(optimisticBonus),
    [optimisticBonus]
  );

  const bonusDescription = useMemo(() => {
    const matchedMission = missions.find(
      (mission) => mission.groupKeyId === groupKey
    );

    return matchedMission?.name || '';
  }, [missions, groupKey]);

  const {
    amountCents,
    amountWagerCents,
    amountWagerRequirementCents,
    freespinsTotal,
    gamesInfo,
  } = useMemo(
    () =>
      isBonus(optimisticBonus)
        ? {
            amountCents: optimisticBonus.amountCents,
            amountWagerCents: optimisticBonus.amountWagerCents,
            amountWagerRequirementCents:
              optimisticBonus.amountWagerRequirementCents,
          }
        : {
            freespinsTotal: optimisticBonus.freespinsTotal,
            gamesInfo: optimisticBonus.gamesInfo,
          },
    [optimisticBonus]
  );

  const timeLeft = useMemo(() => {
    const untilDate = activatable ? activatableUntil : validUntil;

    if (!untilDate) {
      return 0;
    }

    return differenceInSeconds(new Date(untilDate), new Date());
  }, [activatable, activatableUntil, validUntil]);

  const bonusAmount = useMemo(() => {
    if (!amountCents) return;
    const bonusAmountData = getCurrencyCentsAndCode(
      amountCents,
      currency,
      DEFAULT_CURRENCIES
    );

    return amountWithCurrencySymbol(
      bonusAmountData.currency,
      bonusAmountData.amount
    ).toString();
  }, [amountCents, currency]);

  const isWagerFreeCash =
    isCashBonus && !!amountCents && amountWagerRequirementCents === 0;

  const handleActivate = useCallback(async () => {
    const error = await (async () => {
      let responseError;

      try {
        if (isCashBonus) {
          setPendingBonusActivation(true);
          const { error } = await playerBonusesActivation(id);

          if (!error) await fetchBonuses();

          responseError = error;
        } else {
          setPendingFreespinsActivation(true);
          const { error } = await playerFreespinsActivation(id);

          if (!error) {
            setOptimisticBonus({
              ...(optimisticBonus as Freespin),
              activatable: false,
            });

            await fetchFreespins();
          }

          responseError = error;
        }
      } catch (e) {
        console.log(e);
      } finally {
        if (isCashBonus) {
          setPendingBonusActivation(false);
        } else {
          setPendingFreespinsActivation(false);
        }
      }

      return responseError;
    })();

    if (error) {
      showTooltip({
        type: TooltipTypes.ERROR,
        body: t('sthWentWrong'),
      });
    } else if (isWagerFreeCash) {
      showTooltip({
        type: TooltipTypes.SUCCESS,
        body: (
          <WagerFreeCashTooltip
            amountCents={amountCents as number}
            currency={currency}
          />
        ),
        pauseOnHover: true,
      });
    } else {
      showTooltip({
        type: TooltipTypes.SUCCESS,
        body: (
          <div>
            <p className="title">{t('tooltip.bonusActivationSuccess.title')}</p>
            <p>{t('tooltip.bonusActivationSuccess.description')}</p>
          </div>
        ),
      });
    }
  }, [
    amountCents,
    currency,
    fetchBonuses,
    fetchFreespins,
    id,
    isCashBonus,
    isWagerFreeCash,
    t,
  ]);

  const handlePlay = useCallback(() => {
    if (!gameCategoriesList.length) return;

    const link = bonusGames?.length
      ? `/game/${bonusGames[0].fullPath}`
      : '/games/wagering#gamesSection';

    if (user?.currency !== currency && isCashBonus) {
      addUrlParameter(BONUS_ID_QUERY_KEY, String(id), true);
      openModal(Modals.ChangeWageringCurrency, {
        customOnClose: () => navigate(link),
        replaceUrl: true,
      });
      return;
    }

    navigate(link);
  }, [
    bonusGames,
    currency,
    gameCategoriesList,
    id,
    navigate,
    openModal,
    user?.currency,
    isCashBonus,
  ]);

  const wagerInfo = useMemo(() => {
    if (
      amountCents === undefined ||
      amountWagerCents === undefined ||
      !amountWagerRequirementCents
    )
      return;

    const wagerPercent =
      (amountWagerCents * PERCENTS_100) / amountWagerRequirementCents;

    const wagerAmount = `${amountWagerRequirementCents / amountCents}x WG`;

    const { amount: amountWager } = getCurrencyCentsAndCode(
      amountWagerCents,
      currency,
      DEFAULT_CURRENCIES
    );

    const { amount: amountWagerRequirement, currency: code } =
      getCurrencyCentsAndCode(
        amountWagerRequirementCents,
        currency,
        DEFAULT_CURRENCIES
      );

    const wagerProportion = (
      <>
        {amountWager} / {amountWagerRequirement} {code} {t('wagered')}
      </>
    );

    return { wagerPercent, wagerProportion, wagerAmount };
  }, [amountCents, amountWagerCents, amountWagerRequirementCents, currency, t]);

  const isLongText = useMemo(() => {
    const titleLength = bonusAmount
      ? bonusAmount.length
      : t('freespinsX', { freespinsTotal }).length;

    return titleLength > MIN_LONG_TEXT;
  }, [bonusAmount, freespinsTotal, t]);

  useEffect(() => {
    if (!gamesInfo) return;

    (async () => {
      try {
        setPendingBonusGames(true);

        const gameIds = gamesInfo.map(({ identifier }) => identifier);

        const games = await fetchGamesByIds({ gameIds }, isMobileDevice);

        if (!games) return;

        const gamesV1 = mapGameV2ToGameV1(Object.values(games));

        setBonusGames(gamesV1);
      } catch (e) {
        console.log(e);
      } finally {
        setPendingBonusGames(false);
      }
    })();
  }, [fetchGamesByIds, gamesInfo, isMobileDevice]);

  useEffect(() => {
    const queryValue = searchParams.get(BONUS_REF_PARAM);
    if (!queryValue) return;

    setIsBonusSelected(queryValue === bonus.id.toString());

    removeQueryParameter(BONUS_REF_PARAM, push);
  }, [bonus.id, navigate, push, searchParams]);

  useEffect(() => {
    if (isBonusSelected) {
      const timeoutId = setTimeout(() => {
        window.scrollTo({
          behavior: 'smooth',
          top:
            (ref?.current?.getBoundingClientRect().top || 0) -
            PROMO_HEADER_OFFSET,
        });
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [isBonusSelected]);

  return (
    <>
      <ScBonusCard
        isFlipped={isFlipped}
        isLongText={isLongText}
        ref={ref}
        withWager={!!validUntil && !!wagerInfo}
      >
        <div className="front">
          <div className="header">
            <BonusCardTimer timeLeft={timeLeft} />
            <InfoButton handleClick={() => toggleFlip(true)} />
          </div>

          <div className="info">
            <div className="title">{title}</div>
            <div className="offer">
              {bonusAmount}
              {freespinsTotal && t('freespinsX', { freespinsTotal })}
            </div>
            <div
              className={`offer-description${bonusDescription && wagerInfo ? 'full' : ''}`}
            >
              {bonusDescription && (
                <div className="description">
                  <ImageComponent
                    alt="bonus-icon"
                    height={36}
                    src="/bonuses/circle-logo.svg"
                    width={36}
                  />
                  <p>{bonusDescription}</p>
                </div>
              )}
              {validUntil && wagerInfo && (
                <div className="progress">
                  <ProgressBar
                    isFloorRoundedValue
                    mode={ProgressBarMode.SECONDARY}
                    showValue={ValuePosition.RIGHT}
                    value={wagerInfo.wagerPercent}
                  />

                  {isCashBonus && (
                    <div className="wager">
                      <div className="progress">
                        {wagerInfo.wagerProportion}
                      </div>
                      <div className="coef">{wagerInfo.wagerAmount}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <BonusCardButtons
            activatable={activatable}
            cancelable={cancelable}
            handleActivate={handleActivate}
            handlePlay={handlePlay}
            id={id}
            isBonusType={isCashBonus}
            isPending={isPending}
          />

          <ImageComponent
            alt="defualt-bonus-img"
            className="default-bonus-img"
            height={160}
            src={
              isCashBonus
                ? '/bonuses/bonus-card-gift.png'
                : '/bonuses/bonus-fs.webp'
            }
            width={160}
          />
        </div>

        <div className="back">
          <ButtonClose onClick={() => toggleFlip(false)} right={0} top={0} />

          <BonusTerms bonus={optimisticBonus} bonusGames={bonusGames} />

          <BonusCardButtons
            activatable={activatable}
            cancelable={cancelable}
            handleActivate={handleActivate}
            handlePlay={handlePlay}
            id={id}
            isBonusType={isCashBonus}
            isPending={isPending}
          />
        </div>
      </ScBonusCard>
    </>
  );
};
